import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Pagination from '../components/Pagination';
import ExportButton from '../components/ExportButton';
import { useGetServicesQuery, useAddServiceMutation, useUpdateServiceMutation, useDeleteServiceMutation, useGetActiveContractsQuery, useGetActiveServiceTypesQuery } from '../store/api';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner';
import TableHeader from '../components/TableHeader';

export default function ContractServices() {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalServiceCount, setTotalServiceCount] = useState(0);
  const [servicesData, setServicesData] = useState(null);
  const [isExport, setIsExport] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [viewingService, setViewingService] = useState(null);

  
  const [formData, setFormData] = useState({
    contract_id: '',
    service_type_id: '',
    price: '',
    quantity: 1,
    total_amount: '',
    currency: 'AZN',
    notes: ''
  });

  // Get services
  const { data, isError, error, isSuccess, isLoading } = useGetServicesQuery({
    keyword: searchTerm,
    is_export: isExport,
    page: currentPage,
    limit: itemsPerPage
  });

  // Get contracts for select
  const { data: contractsData, isLoading: contractsLoading } = useGetActiveContractsQuery({});
  
  // Get service types for select
  const { data: serviceTypesData, isLoading: serviceTypesLoading } = useGetActiveServiceTypesQuery({});
  


  useEffect(() => {
    if (isExport == 1) {
      console.log('Export data:', data);
      if (data && data.data && Array.isArray(data.data)) {
        const transformedData = data.data.map(service => ({
          'Xidmət Adı': service.service_type.name,
          'Təsvir': service.service_type.description,
          'Qiymət': service.price,
          'Valyuta': service.currency,
          'Miqdar': service.quantity,
          'Ümumi Məbləğ': service.total_amount,
          'Müqavilə Nömrəsi': service.contract.contract_number,
          'Yaradılma Tarixi': new Date(service.created_at).toLocaleString("az-AZ"),
          'ID': service.id
        }));

        setExportData(transformedData);
        console.log('Transformed data:', transformedData);
        setIsExport(0); // Reset export flag
      } else {
        console.error('Export data not available or not in correct format:', data);
        setExportData([]);
        setIsExport(0); // Reset export flag
      }
    } else {
      if (!isLoading && data) {
        setServicesData(data.data.data);
        setTotalPages(Math.ceil(data.data.total / data.data.per_page));
        setTotalServiceCount(data.data.total);
      }
    }
  }, [data, isSuccess, isError, isLoading, error]);

  // Add service
  const [addService, {
    data: createResponse,
    isError: createIsError,
    error: createError,
    isSuccess: createIsSuccess,
    isLoading: createIsLoading
  }] = useAddServiceMutation();

  useEffect(() => {
    if (!createIsLoading && createResponse) {
      toast.success(createResponse.message);
      setIsModalOpen(false);
      setFormData({
        contract_id: '',
        service_type_id: '',
        price: '',
        quantity: 1,
        total_amount: '',
        currency: 'AZN',
        notes: ''
      });
    }

    if (createIsError) {
      let errorMessage = "Xəta baş verdi";
      if (createError && createError.data && createError.data.errors) {
        const errors = createError.data.errors;
        const errorMessages = [];
        
        Object.keys(errors).forEach(key => {
          if (Array.isArray(errors[key])) {
            errorMessages.push(...errors[key]);
          } else {
            errorMessages.push(errors[key]);
          }
        });
        
        errorMessage = errorMessages.join(', ');
      } else if (createError && createError.data && createError.data.message) {
        errorMessage = createError.data.message;
      } else if (createError && createError.message) {
        errorMessage = createError.message;
      }
      
      toast.error(errorMessage);
    }
  }, [createResponse, createIsSuccess, createIsError, createIsLoading, createError]);

  // Update service
  const [updateService, {
    data: updateData,
    isError: updateIsError,
    error: updateError,
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading
  }] = useUpdateServiceMutation();

  useEffect(() => {
    if (!updateIsLoading && updateData) {
      toast.success(updateData.message);
      setIsModalOpen(false);
      setEditingService(null);
      setFormData({
        contract_id: '',
        service_type_id: '',
        price: '',
        quantity: 1,
        total_amount: '',
        currency: 'AZN',
        notes: ''
      });
    }

    if (updateIsError) {
      let errorMessage = "Yeniləmə uğursuz oldu";
      if (updateError && updateError.data && updateError.data.errors) {
        const errors = updateError.data.errors;
        const errorMessages = [];
        
        Object.keys(errors).forEach(key => {
          if (Array.isArray(errors[key])) {
            errorMessages.push(...errors[key]);
          } else {
            errorMessages.push(errors[key]);
          }
        });
        
        errorMessage = errorMessages.join(', ');
      } else if (updateError && updateError.data && updateError.data.message) {
        errorMessage = updateError.data.message;
      } else if (updateError && updateError.message) {
        errorMessage = updateError.message;
      }
      
      toast.error(errorMessage);
    }
  }, [updateData, updateIsSuccess, updateIsError, updateIsLoading, updateError]);

  // Delete service
  const [deleteService, {
    data: deleteRes,
    isError: deleteIsError,
    error: deleteError,
    isSuccess: deleteIsSuccess,
    isLoading: deleteIsLoading
  }] = useDeleteServiceMutation();

  useEffect(() => {
    if (!deleteIsLoading && deleteRes) {
      toast.success(deleteRes.message);
    }
    
    if (deleteIsError) {
      let errorMessage = "Silmə uğursuz oldu";
      if (deleteError && deleteError.data && deleteError.data.errors) {
        const errors = deleteError.data.errors;
        const errorMessages = [];
        
        Object.keys(errors).forEach(key => {
          if (Array.isArray(errors[key])) {
            errorMessages.push(...errors[key]);
          } else {
            errorMessages.push(errors[key]);
          }
        });
        
        errorMessage = errorMessages.join(', ');
      } else if (deleteError && deleteError.data && deleteError.data.message) {
        errorMessage = deleteError.data.message;
      } else if (deleteError && deleteError.message) {
        errorMessage = deleteError.message;
      }
      
      toast.error(errorMessage);
    }
  }, [deleteRes, deleteIsError, deleteError, deleteIsLoading]);

  // Calculate total amount
  const calculateTotalAmount = (price, quantity) => {
    const numPrice = parseFloat(price) || 0;
    const numQuantity = parseInt(quantity) || 1;
    return (numPrice * numQuantity).toFixed(2);
  };

  const handleAdd = () => {
    setEditingService(null);
    setFormData({
      contract_id: '',
      service_type_id: '',
      price: '',
      quantity: 1,
      total_amount: '',
      currency: 'AZN',
      notes: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    const calculatedTotal = calculateTotalAmount(service.price, service.quantity);
    setFormData({
      contract_id: service.contract_id,
      service_type_id: service.service_type_id,
      price: service.price.toString(),
      quantity: service.quantity,
      total_amount: calculatedTotal,
      currency: service.currency,
      notes: service.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleView = (service) => {
    setViewingService(service);
    setIsViewModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (editingService) {
      updateService({ id: editingService?.id, obj: formData });
    } else {
      addService(formData);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = (serviceId) => {
    if (window.confirm('Bu xidməti silmək istədiyinizə əminsiniz?')) {
      deleteService(serviceId);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };



  const exportHeaders = ['Xidmət Adı', 'Təsvir', 'Qiymət', 'Valyuta', 'Miqdar', 'Ümumi Məbləğ', 'Müqavilə Nömrəsi', 'Yaradılma Tarixi', 'ID'];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Audio
          height="80"
          width="80"
          radius="9"
          color="blue"
          ariaLabel="loading"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Müqavilə Xidmətləri</h1>
          <p className="mt-1 text-sm text-gray-600">
            Müqavilələrdə istifadə olunan xidmətləri idarə edin
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {totalServiceCount}
            </span>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <ExportButton 
            setIsExport={setIsExport}
            data={exportData}
            filename="muqavile-xidmetleri"
            headers={exportHeaders}
          />
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Yeni Xidmət
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Xidmət adı, təsvir..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            onClick={handleClearFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Təmizlə
          </button>
        </div>
      </div>

      {/* Services table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Xidmət
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Qiymət
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Miqdar
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Ümumi Məbləğ
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Müqavilə
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {servicesData && servicesData.map((service) => (
                  <tr key={service.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-3">
                          <CurrencyDollarIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                            {service.service_type.name}
                          </div>
                          <div className="text-xs text-gray-500 max-w-xs truncate">
                            {service.service_type.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {service.price} {service.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {service.quantity}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {service.total_amount} {service.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {service.contract.contract_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleView(service)}
                          className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                          title="Ətraflı bax"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(service)}
                          className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                          title="Redaktə et"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                          title="Sil"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(!servicesData || servicesData.length === 0) && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <CurrencyDollarIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Heç bir xidmət tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni xidmət əlavə edin.
              </p>
              <button
                onClick={handleAdd}
                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                İlk Xidməti Əlavə Et
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalServiceCount}
        itemsPerPage={itemsPerPage}
      />

      {/* Add/Edit Service Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingService ? 'Xidməti Redaktə Et' : 'Yeni Xidmət Əlavə Et'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Contract Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Müqavilə *
                    </label>
                    <select
                      value={formData.contract_id}
                      onChange={(e) => setFormData({...formData, contract_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Müqavilə seçin</option>
                      {contractsData?.data?.map(contract => (
                        <option key={contract.id} value={contract.id}>
                          {contract.contract_number} - {contract.student?.name || 'Tələbə'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Service Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Xidmət Tipi *
                    </label>
                    <select
                      value={formData.service_type_id}
                      onChange={(e) => {
                        const selectedServiceType = serviceTypesData?.data?.find(st => st.id == e.target.value);
                        const newPrice = selectedServiceType?.price || '';
                        const newCurrency = selectedServiceType?.currency || 'AZN';
                        const newTotalAmount = calculateTotalAmount(newPrice, formData.quantity);
                        setFormData({
                          ...formData, 
                          service_type_id: e.target.value,
                          price: newPrice,
                          currency: newCurrency,
                          total_amount: newTotalAmount
                        });
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Xidmət tipi seçin</option>
                      {serviceTypesData?.data?.map(serviceType => (
                        <option key={serviceType.id} value={serviceType.id}>
                          {serviceType.name} - {serviceType.price} {serviceType.currency}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Qiymət *
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => {
                        const newPrice = e.target.value;
                        const newTotalAmount = calculateTotalAmount(newPrice, formData.quantity);
                        setFormData({
                          ...formData, 
                          price: newPrice,
                          total_amount: newTotalAmount
                        });
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Miqdar *
                    </label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => {
                        const newQuantity = e.target.value;
                        const newTotalAmount = calculateTotalAmount(formData.price, newQuantity);
                        setFormData({
                          ...formData, 
                          quantity: newQuantity,
                          total_amount: newTotalAmount
                        });
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      min="1"
                      placeholder="1"
                    />
                  </div>

                  {/* Total Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ümumi Məbləğ *
                    </label>
                    <input
                      type="number"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                      required
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      readOnly
                    />
                    <p className="mt-1 text-xs text-gray-500">Qiymət və miqdar əsasında avtomatik hesablanır</p>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valyuta *
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({...formData, currency: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="AZN">AZN</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qeydlər
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Əlavə qeydlər..."
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    disabled={createIsLoading || updateIsLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createIsLoading || updateIsLoading ? 'Saxlanılır...' : (editingService ? 'Yenilə' : 'Saxla')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Service Modal */}
      {isViewModalOpen && viewingService && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Xidmət Məlumatları
                </h3>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Xidmət Adı</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingService.service_type.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Müqavilə</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingService.contract.contract_number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Qiymət</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingService.price} {viewingService.currency}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Miqdar</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingService.quantity}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ümumi Məbləğ</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingService.total_amount} {viewingService.currency}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Valyuta</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingService.currency}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Xidmət Təsviri</label>
                  <p className="mt-1 text-sm text-gray-900">{viewingService.service_type.description || 'Təsvir yoxdur'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Qeydlər</label>
                  <p className="mt-1 text-sm text-gray-900">{viewingService.notes || 'Qeyd yoxdur'}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Yaradılma Tarixi</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(viewingService.created_at).toLocaleString('az-AZ')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Son Yeniləmə</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(viewingService.updated_at).toLocaleString('az-AZ')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Bağla
                </button>
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    handleEdit(viewingService);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Redaktə Et
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
} 